export const StateColors = {
  // Primary States
  SUCCESS: '#0ada45',
  ERROR: '#f40909',
  WARNING: '#f4c109',
  IN_PROGRESS: '#ff6b00',

  // Special States
  SIMULATION: '#20a4f3',
  STANDBY: '#0f7b8c',
  OFFLINE: '#6b7280',
  NEUTRAL: '#e4e4e4',
  DEFAULT: '#4b5563',

  // Signal Quality Gradient
  SIGNAL_EXCELLENT: '#0ada45',
  SIGNAL_GOOD: '#90ee90',
  SIGNAL_FAIR: '#f4c109',
  SIGNAL_POOR: '#ff6b00',
  SIGNAL_NONE: '#f40909',
} as const

export type StateColorKey = keyof typeof StateColors
export type StateColorValue = (typeof StateColors)[StateColorKey]
export const getSignalQualityColor = (quality: number | undefined): string => {
  if (!quality || quality === 0) return StateColors.SIGNAL_NONE
  if (quality === 1) return StateColors.SIGNAL_NONE
  if (quality === 2) return StateColors.SIGNAL_POOR
  if (quality === 3) return StateColors.SIGNAL_FAIR
  if (quality === 4) return StateColors.SIGNAL_GOOD
  if (quality === 5) return StateColors.SIGNAL_EXCELLENT
  return StateColors.SIGNAL_NONE
}
export const getLinkStateColor = (state: number | undefined): string => {
  return state === 1 ? StateColors.SUCCESS : StateColors.ERROR
}
export const getBatteryColor = (battery: number | undefined): string => {
  if (!battery) return StateColors.ERROR
  if (battery > 50) return StateColors.SUCCESS
  if (battery > 20) return StateColors.WARNING
  return StateColors.ERROR
}

export const getUavStateColor = (
  isSimulatorEnabled: boolean | undefined,
  droneStatus: string | undefined,
): string => {
  if (isSimulatorEnabled === true && droneStatus === 'ON') return StateColors.SIMULATION
  return droneStatus !== 'ON' ? StateColors.ERROR : StateColors.SUCCESS
}

/**
 * Wind speed bands, in true m/s (FLY3D-945): green below 10, amber 10 to 12
 * inclusive, red from 12.1 up.
 */
export const WIND_SPEED_AMBER_MS = 10
export const WIND_SPEED_RED_MS = 12

/**
 * Colour for a wind speed readout. A missing reading is NEUTRAL rather than
 * green — before the first high-frequency packet arrives there is nothing to
 * reassure the pilot about. Note that 0 m/s is a valid calm reading and must
 * come back green; guarding this with a truthiness check painted calm wind red
 * (FLY3D-945, FLY3D-732).
 */
export const getWindColor = (windSpeed: number | null | undefined): string => {
  if (windSpeed == null || !Number.isFinite(Number(windSpeed))) return StateColors.NEUTRAL
  const speed = Number(windSpeed)
  if (speed < WIND_SPEED_AMBER_MS) return StateColors.SUCCESS
  if (speed <= WIND_SPEED_RED_MS) return StateColors.WARNING
  return StateColors.ERROR
}

/** Wind speed for display: one decimal, or `--` when there is no reading. */
export const formatWindSpeed = (windSpeed: number | null | undefined): string => {
  if (windSpeed == null || !Number.isFinite(Number(windSpeed))) return '--'
  return Number(windSpeed).toFixed(1)
}

export const getRcSignalColor = (signal: number | undefined): string => {
  if (!signal || signal < 40) return StateColors.ERROR
  if (signal <= 60) return StateColors.WARNING
  return StateColors.SUCCESS
}

export const getSatelliteLockColor = (count: number | undefined): string => {
  if (!count || count <= 7) return StateColors.ERROR
  if (count <= 10) return StateColors.WARNING
  return StateColors.SUCCESS
}

export const getVideoLinkModeColor = (
  mode: number | undefined,
  fourGQuality: number | undefined,
  sdrQuality: number | undefined,
): string => {
  if (mode === undefined || mode === null) return StateColors.ERROR
  // Colour by the quality of the link the mode is actually carrying video over.
  const quality = mode === 1 ? fourGQuality : sdrQuality
  if (!quality || quality <= 2) return StateColors.ERROR
  if (quality === 3) return StateColors.WARNING
  return StateColors.SUCCESS
}
